import Hoverable from '@omni/kit/components/Hoverable';
import KitFadeView from '@omni/kit/components/KitFadeView';
import Durations from '@omni/kit/constants/Durations';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { transparentHexColor } from '@omni/kit/utilities/utilities';
import React, {
  ReactElement,
  RefObject,
  useCallback,
  useRef,
  useState,
} from 'react';
import { Platform, StyleSheet, View, ViewStyle } from 'react-native';

import PlayerButton, { PlayerButtonSizes } from '../PlayerButton';
import { SettingsControlRef } from '../SettingsControl/SettingsControl.types';
import VolumeSlider from './VolumeSlider';

interface VolumeControlProps {
  onUpdateMute?: (shouldMute: boolean) => void;
  onUpdateVolume?: (volume: number) => void;
  isMuted?: boolean;
  isVisible?: boolean;
  sliderContainerStyle?: ViewStyle;
  sliderStyle?: ViewStyle;
  settingsControlRef?: RefObject<SettingsControlRef | null>;
  onOpen?: (value: boolean) => void;
  volume?: number;
}

export default function VolumeControl({
  onUpdateMute,
  onUpdateVolume,
  isMuted = false,
  isVisible = true,
  sliderContainerStyle,
  sliderStyle,
  settingsControlRef,
  onOpen,
  volume = 1,
}: VolumeControlProps): ReactElement {
  const closeMenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [isHovering, setIsHovering] = useState(false);

  const onSlideVolumeSlider = useCallback(
    (newVolume: number) => {
      onUpdateMute?.(newVolume === 0);
      onUpdateVolume?.(newVolume);
    },
    [onUpdateVolume, onUpdateMute]
  );

  const onToggleMute = useCallback(() => {
    onUpdateMute?.(!isMuted);
  }, [isMuted, onUpdateMute]);

  const onHoverIn = useCallback(() => {
    onOpen?.(true);
    if (settingsControlRef?.current?.isMenuOpen) {
      /**
       * Added timeout to prevent volume slider showing by mistake when menu is open
       */
      const timeout = setTimeout(() => {
        setIsHovering(true);
        settingsControlRef?.current?.toggleMenu(false);
      }, Durations.m);
      closeMenuTimeoutRef.current = timeout;
    } else {
      setIsHovering(true);
    }
  }, [onOpen, settingsControlRef]);

  const onHoverOut = useCallback(() => {
    onOpen?.(false);
    if (closeMenuTimeoutRef.current) {
      setIsHovering(false);
      clearTimeout(closeMenuTimeoutRef.current);
      closeMenuTimeoutRef.current = null;
    } else {
      setIsHovering(false);
    }
  }, [onOpen]);

  return (
    <KitFadeView show={isVisible} keepElementSize>
      <Hoverable onHoverIn={onHoverIn} onHoverOut={onHoverOut}>
        <View style={styles.contianer}>
          <KitFadeView show={isHovering} style={styles.sliderPopup}>
            <VolumeSlider
              volume={isMuted ? 0 : volume}
              onChange={onSlideVolumeSlider}
              sliderContainerStyle={sliderContainerStyle}
              sliderStyle={sliderStyle}
            />
          </KitFadeView>

          <PlayerButton
            icon={
              isMuted ? 'mute' : volume <= 0.5 ? 'volume-partial' : 'volume'
            }
            onPress={onToggleMute}
            size={
              Platform.OS === 'web'
                ? PlayerButtonSizes.small
                : PlayerButtonSizes.medium
            }
            frameless
          />
        </View>
      </Hoverable>
    </KitFadeView>
  );
}

const styles = StyleSheet.create({
  contianer: {
    marginLeft: 'auto',
    justifyContent: 'center',
    alignItems: 'center',
  },
  sliderPopup: {
    paddingHorizontal: 0,
    marginVertical: Spacing.s,
    backgroundColor: transparentHexColor(Colors.N1000, 80),
    borderRadius: Spacing.xl,
    position: 'absolute',
    paddingVertical: Spacing.l,
    bottom: Spacing.xxl + Spacing.xs,
    zIndex: 2,
  },
});
